import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const CashRegister = () => {
  const [status, setStatus] = useState(null);
  const [detailedSummary, setDetailedSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showClosePreviousModal, setShowClosePreviousModal] = useState(false);
  const [showCloseReportModal, setShowCloseReportModal] = useState(false);
  const [selectedCloseReport, setSelectedCloseReport] = useState(null);
  const [previousRegisterSummary, setPreviousRegisterSummary] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [unclosedRegister, setUnclosedRegister] = useState(null);
  const [openingBalance, setOpeningBalance] = useState('');
  const [openingNotes, setOpeningNotes] = useState('');
  const [physicalCash, setPhysicalCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [previousPhysicalCash, setPreviousPhysicalCash] = useState('');
  const [previousClosingNotes, setPreviousClosingNotes] = useState('');
  const [transaction, setTransaction] = useState({ type: 'cash_in', amount: '', description: '', reference: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchStatus();
    fetchHistory();
    fetchDetailedSummary();
    fetchBusinessInfo();
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
    } catch (err) {
      console.error('Error fetching business info:', err);
    }
  };

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/cash-register/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedSummary = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/cash-register/detailed-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailedSummary(response.data);
    } catch (err) {
      console.error('Error fetching detailed summary:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/cash-register/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
      
      // Check for unclosed registers from previous days
      const today = new Date().toISOString().split('T')[0];
      const unclosed = response.data.find(r => !r.closed_at && r.date.split('T')[0] < today);
      setUnclosedRegister(unclosed);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const handleOpen = async () => {
    if (!openingBalance) {
      setError('Please enter opening balance');
      return;
    }

    // Check for unclosed registers from previous days
    if (unclosedRegister) {
      setError(`Please close the register from ${new Date(unclosedRegister.date).toLocaleDateString('en-IN')} before opening a new one.`);
      return;
    }

    setError('');
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/cash-register/open`, {
        opening_balance: parseFloat(openingBalance),
        notes: openingNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Cash register opened successfully');
      setShowOpenModal(false);
      setOpeningBalance('');
      setOpeningNotes('');
      fetchStatus();
      fetchHistory();
      fetchDetailedSummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to open register');
    }
  };

  const handleClose = async () => {
    if (!physicalCash) {
      setError('Please enter physical cash count');
      return;
    }

    setError('');
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/cash-register/close`, {
        physical_cash: parseFloat(physicalCash),
        notes: closingNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(`Cash register closed. Difference: ₹${(response.data.summary?.difference ?? response.data.difference ?? 0).toFixed(2)}`);
      setShowCloseModal(false);
      setPhysicalCash('');
      setClosingNotes('');
      fetchStatus();
      fetchHistory();
      fetchDetailedSummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to close register');
    }
  };

  const handleOpenClosePreviousModal = async () => {
    if (!unclosedRegister) return;
    
    try {
      const token = localStorage.getItem('pos_token');
      // Fetch summary for the unclosed register date
      const response = await axios.get(`${API}/cash-register/summary-for-date`, {
        params: { date: unclosedRegister.date.split('T')[0] },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviousRegisterSummary(response.data);
    } catch (err) {
      // If API doesn't exist, calculate from the register data
      setPreviousRegisterSummary({
        opening_balance: unclosedRegister.opening_balance || 0,
        expected_cash: unclosedRegister.expected_cash || unclosedRegister.opening_balance || 0,
        date: unclosedRegister.date
      });
    }
    setShowClosePreviousModal(true);
  };

  const handleClosePreviousRegister = async () => {
    if (!previousPhysicalCash) {
      setError('Please enter actual cash count');
      return;
    }

    setError('');
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/cash-register/close-previous`, {
        register_date: unclosedRegister.date.split('T')[0],
        physical_cash: parseFloat(previousPhysicalCash),
        notes: previousClosingNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const diff = response.data.difference;
      setSuccess(`Previous register closed. Difference: ₹${diff?.toFixed(2) || '0.00'}`);
      setShowClosePreviousModal(false);
      setPreviousPhysicalCash('');
      setPreviousClosingNotes('');
      setPreviousRegisterSummary(null);
      setUnclosedRegister(null);
      fetchStatus();
      fetchHistory();
      fetchDetailedSummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to close previous register');
    }
  };

  // View close report for a register
  const handleViewReport = (register) => {
    setSelectedCloseReport(register);
    setShowCloseReportModal(true);
  };

  // Export close report to PDF - Professional Styling
  const handleExportReportPDF = (register) => {
    const summary = register.closing_summary || {};
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = 20;

    // Helper function for section headers
    const drawSectionHeader = (title, yPos) => {
      pdf.setFillColor(79, 70, 229); // Violet-600
      pdf.rect(margin, yPos - 4, contentWidth, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(title, margin + 4, yPos + 1);
      pdf.setTextColor(0, 0, 0);
      return yPos + 10;
    };

    // Helper function for data rows
    const drawDataRow = (label, value, yPos, indent = 0) => {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(label, margin + 4 + indent, yPos);
      pdf.setTextColor(30, 30, 30);
      pdf.setFont('helvetica', 'bold');
      pdf.text(value, pageWidth - margin - 4, yPos, { align: 'right' });
      pdf.setFont('helvetica', 'normal');
      return yPos + 6;
    };

    // Header Box with Business Info
    pdf.setFillColor(248, 250, 252);
    pdf.roundedRect(margin, y - 5, contentWidth, 30, 3, 3, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.roundedRect(margin, y - 5, contentWidth, 30, 3, 3, 'S');
    
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 41, 59);
    pdf.text(businessInfo?.business_name || 'Business Name', pageWidth / 2, y + 5, { align: 'center' });
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139);
    const address = [businessInfo?.address, businessInfo?.city, businessInfo?.state].filter(Boolean).join(', ');
    if (address) {
      pdf.text(address, pageWidth / 2, y + 12, { align: 'center' });
    }
    if (businessInfo?.gst_number) {
      pdf.text(`GSTIN: ${businessInfo.gst_number}`, pageWidth / 2, y + 18, { align: 'center' });
    }
    y += 35;

    // Title
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(79, 70, 229);
    pdf.text('CASH REGISTER CLOSE REPORT', pageWidth / 2, y, { align: 'center' });
    y += 4;
    
    // Date subtitle
    const reportDate = new Date(register.closed_at).toLocaleDateString('en-IN', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139);
    pdf.text(reportDate, pageWidth / 2, y + 4, { align: 'center' });
    y += 15;

    // Register Details Section
    y = drawSectionHeader('REGISTER DETAILS', y);
    y = drawDataRow('Opening Balance', formatCurrency(summary.opening_balance || register.opening_balance), y);
    y = drawDataRow('Opened At', new Date(register.opened_at).toLocaleString('en-IN'), y);
    y = drawDataRow('Opened By', register.opened_by || 'Owner', y);
    y += 5;

    // Sales Summary Section
    y = drawSectionHeader('SALES SUMMARY', y);
    const sales = summary.sales || {};
    y = drawDataRow(`Total Sales (${sales.count || 0} bills)`, formatCurrency(sales.total || 0), y);
    
    // Sales breakdown with light background
    pdf.setFillColor(249, 250, 251);
    pdf.rect(margin, y - 2, contentWidth, 22, 'F');
    y = drawDataRow('Cash Sales', formatCurrency(sales.by_mode?.cash || 0), y, 8);
    y = drawDataRow('UPI Sales', formatCurrency(sales.by_mode?.upi || 0), y, 8);
    y = drawDataRow('Card Sales', formatCurrency(sales.by_mode?.card || 0), y, 8);
    y = drawDataRow('Credit Sales', formatCurrency(sales.by_mode?.credit || 0), y, 8);
    y += 5;

    // Expenses Summary Section
    y = drawSectionHeader('EXPENSES SUMMARY', y);
    const expenses = summary.expenses || {};
    y = drawDataRow(`Total Expenses (${expenses.count || 0} entries)`, formatCurrency(expenses.total || 0), y);
    
    pdf.setFillColor(249, 250, 251);
    pdf.rect(margin, y - 2, contentWidth, 16, 'F');
    y = drawDataRow('Cash Expenses', formatCurrency(expenses.by_mode?.cash || 0), y, 8);
    y = drawDataRow('Bank/UPI Expenses', formatCurrency((expenses.by_mode?.bank || 0) + (expenses.by_mode?.upi || 0)), y, 8);
    y += 5;

    // Supplier Payments Section (if any)
    if (summary.supplier_payments?.total > 0) {
      y = drawSectionHeader('SUPPLIER PAYMENTS', y);
      const suppPay = summary.supplier_payments || {};
      y = drawDataRow('Total Supplier Payments', formatCurrency(suppPay.total || 0), y);
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, y - 2, contentWidth, 10, 'F');
      y = drawDataRow('Cash Payments', formatCurrency(suppPay.by_mode?.cash || 0), y, 8);
      y += 5;
    }

    // Customer Collections Section (if any)
    if (summary.customer_payments?.total > 0) {
      y = drawSectionHeader('CUSTOMER COLLECTIONS', y);
      const custPay = summary.customer_payments || {};
      y = drawDataRow('Total Collections', formatCurrency(custPay.total || 0), y);
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, y - 2, contentWidth, 10, 'F');
      y = drawDataRow('Cash Collections', formatCurrency(custPay.by_mode?.cash || 0), y, 8);
      y += 5;
    }

    // Manual Transactions Section (if any)
    if ((summary.transactions?.cash_in || 0) > 0 || (summary.transactions?.cash_out || 0) > 0) {
      y = drawSectionHeader('MANUAL TRANSACTIONS', y);
      y = drawDataRow('Cash In', formatCurrency(summary.transactions?.cash_in || 0), y);
      y = drawDataRow('Cash Out', formatCurrency(summary.transactions?.cash_out || 0), y);
      y += 5;
    }

    // Closing Summary Box - Highlighted
    y += 3;
    const closingSummaryHeight = 42;
    pdf.setFillColor(239, 246, 255); // Blue-50
    pdf.setDrawColor(147, 197, 253); // Blue-300
    pdf.roundedRect(margin, y - 3, contentWidth, closingSummaryHeight, 3, 3, 'FD');
    
    y += 3;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175); // Blue-800
    pdf.text('CLOSING SUMMARY', margin + 4, y);
    y += 8;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(55, 65, 81);
    pdf.text('Expected Cash:', margin + 8, y);
    pdf.setFont('helvetica', 'bold');
    pdf.text(formatCurrency(summary.expected_cash || register.expected_cash), pageWidth - margin - 8, y, { align: 'right' });
    y += 7;
    
    pdf.setFont('helvetica', 'normal');
    pdf.text('Actual Cash (Physical Count):', margin + 8, y);
    pdf.setFont('helvetica', 'bold');
    pdf.text(formatCurrency(summary.physical_cash || register.physical_cash), pageWidth - margin - 8, y, { align: 'right' });
    y += 7;
    
    const diff = (summary.difference !== undefined ? summary.difference : register.difference) || 0;
    pdf.setFont('helvetica', 'normal');
    pdf.text('Difference:', margin + 8, y);
    
    // Color code the difference
    if (diff > 0) {
      pdf.setTextColor(22, 163, 74); // Green-600
    } else if (diff < 0) {
      pdf.setTextColor(220, 38, 38); // Red-600
    } else {
      pdf.setTextColor(55, 65, 81);
    }
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${formatCurrency(diff)} ${diff > 0 ? '(Excess)' : diff < 0 ? '(Short)' : '(Exact)'}`, pageWidth - margin - 8, y, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
    
    y += closingSummaryHeight - 25;

    // Closing Info
    y += 8;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139);
    pdf.text(`Closed: ${new Date(register.closed_at).toLocaleString('en-IN')}`, margin, y);
    pdf.text(`By: ${register.closed_by || 'Owner'}`, pageWidth - margin, y, { align: 'right' });

    // Notes (if any)
    if (register.closing_notes) {
      y += 8;
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      pdf.text('Notes:', margin, y);
      y += 5;
      pdf.setTextColor(55, 65, 81);
      const noteLines = pdf.splitTextToSize(register.closing_notes, contentWidth - 10);
      pdf.text(noteLines, margin + 4, y);
    }

    // Footer
    y = pdf.internal.pageSize.getHeight() - 12;
    pdf.setDrawColor(226, 232, 240);
    pdf.line(margin, y - 5, pageWidth - margin, y - 5);
    pdf.setFontSize(8);
    pdf.setTextColor(148, 163, 184);
    pdf.text(`Generated by GPOS • ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, y, { align: 'center' });

    // Save
    const fileName = `Cash_Register_Report_${new Date(register.closed_at).toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
    setSuccess('Report exported successfully!');
  };

  const handleTransaction = async () => {
    if (!transaction.amount || !transaction.description) {
      setError('Please enter amount and description');
      return;
    }

    setError('');
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/cash-register/transaction`, {
        type: transaction.type,
        amount: parseFloat(transaction.amount),
        description: transaction.description,
        reference: transaction.reference
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Transaction added successfully');
      setShowTransactionModal(false);
      setTransaction({ type: 'cash_in', amount: '', description: '', reference: '' });
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add transaction');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="cash-register-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Cash Register</h1>
          <p className="text-slate-500">Manage daily cash flow</p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
        </div>
      )}

      {/* Unclosed Register Warning */}
      {unclosedRegister && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-medium text-amber-800">Unclosed Register Found</p>
                <p className="text-sm text-amber-700">
                  Register from <strong>{new Date(unclosedRegister.date).toLocaleDateString('en-IN')}</strong> was not closed. Please close it before opening a new one.
                </p>
              </div>
            </div>
            <button
              onClick={handleOpenClosePreviousModal}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm whitespace-nowrap"
              data-testid="close-previous-btn"
            >
              Close Now
            </button>
          </div>
        </div>
      )}

      {/* Current Status */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className={`p-6 ${status?.status === 'open' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-slate-500 to-slate-600'}`}>
          <div className="flex items-center justify-between text-white">
            <div>
              <p className="text-sm opacity-90">Register Status</p>
              <p className="text-3xl font-bold mt-1 capitalize">{status?.status || 'Closed'}</p>
            </div>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${status?.status === 'open' ? 'bg-white/20' : 'bg-white/10'}`}>
              {status?.status === 'open' ? (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              )}
            </div>
          </div>
        </div>

        {status?.status === 'open' && (
          <div className="p-6 space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500">Opening Balance</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  {formatCurrency(status.opening_balance)}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Opened: {formatDate(status.opened_at)}
                </p>
              </div>

              <div className="p-4 bg-violet-50 rounded-xl">
                <p className="text-sm text-violet-600">Expected Cash</p>
                <p className="text-2xl font-bold text-violet-700 mt-1">
                  {formatCurrency(status.expected_cash)}
                </p>
                <p className="text-xs text-violet-400 mt-1">
                  Based on transactions
                </p>
              </div>

              <div className="p-4 bg-emerald-50 rounded-xl">
                <p className="text-sm text-emerald-600">Transactions Today</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">
                  {status.transactions?.length || 0}
                </p>
              </div>
            </div>

            {/* Detailed Breakdown */}
            {detailedSummary && detailedSummary.status === 'open' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* Sales Breakdown */}
                <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                  <h4 className="font-semibold text-green-800 mb-3">Sales Summary ({detailedSummary.bill_count} bills)</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-600">Cash Sales:</span>
                      <span className="font-medium">{formatCurrency(detailedSummary.sales?.by_mode?.cash || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600">UPI Sales:</span>
                      <span className="font-medium">{formatCurrency(detailedSummary.sales?.by_mode?.upi || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600">Card Sales:</span>
                      <span className="font-medium">{formatCurrency(detailedSummary.sales?.by_mode?.card || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-orange-600">Credit Sales:</span>
                      <span className="font-medium text-orange-600">{formatCurrency(detailedSummary.sales?.by_mode?.credit || 0)}</span>
                    </div>
                    <div className="flex justify-between border-t border-green-200 pt-2 mt-2">
                      <span className="font-semibold text-green-800">Total Sales:</span>
                      <span className="font-bold text-green-800">{formatCurrency(detailedSummary.sales?.total || 0)}</span>
                    </div>
                  </div>
                </div>

                {/* Expenses & Outflows */}
                <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                  <h4 className="font-semibold text-red-800 mb-3">Expenses & Outflows ({detailedSummary.expense_count} entries)</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-red-600">Cash Expenses:</span>
                      <span className="font-medium">{formatCurrency(detailedSummary.expenses?.by_mode?.cash || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">Bank Expenses:</span>
                      <span className="font-medium">{formatCurrency(detailedSummary.expenses?.by_mode?.bank || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">UPI Expenses:</span>
                      <span className="font-medium">{formatCurrency(detailedSummary.expenses?.by_mode?.upi || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Cash Out (Manual):</span>
                      <span className="font-medium">{formatCurrency(detailedSummary.transactions?.cash_out || 0)}</span>
                    </div>
                    <div className="flex justify-between border-t border-red-200 pt-2 mt-2">
                      <span className="font-semibold text-red-800">Total Expenses:</span>
                      <span className="font-bold text-red-800">{formatCurrency(detailedSummary.expenses?.total || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cash Calculation Formula */}
            {detailedSummary && detailedSummary.status === 'open' && (
              <div className="p-4 bg-slate-100 rounded-xl mt-4">
                <h4 className="font-semibold text-slate-700 mb-2">Cash Calculation</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>Opening Balance: {formatCurrency(detailedSummary.opening_balance)}</p>
                  <p className="text-green-600">+ Cash Sales: {formatCurrency(detailedSummary.sales?.by_mode?.cash || 0)}</p>
                  <p className="text-green-600">+ Cash In (Manual): {formatCurrency(detailedSummary.transactions?.cash_in || 0)}</p>
                  <p className="text-green-600">+ Credit Collection: {formatCurrency(detailedSummary.credit_collection || 0)}</p>
                  <p className="text-red-600">- Cash Expenses: {formatCurrency(detailedSummary.expenses?.by_mode?.cash || 0)}</p>
                  <p className="text-red-600">- Cash Out (Manual): {formatCurrency(detailedSummary.transactions?.cash_out || 0)}</p>
                  <p className="text-red-600">- Supplier Payments: {formatCurrency(detailedSummary.supplier_payments || 0)}</p>
                  <p className="font-bold text-violet-700 pt-2 border-t border-slate-300 mt-2">
                    = Expected Cash: {formatCurrency(detailedSummary.expected_cash)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="p-6 border-t border-slate-200 flex flex-wrap gap-3">
          {status?.status !== 'open' ? (
            <button
              onClick={() => setShowOpenModal(true)}
              className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
              data-testid="open-register-btn"
            >
              Open Register
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowTransactionModal(true)}
                className="px-6 py-2.5 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-all"
                data-testid="add-transaction-btn"
              >
                Add Transaction
              </button>
              <button
                onClick={() => setShowCloseModal(true)}
                className="px-6 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-all"
                data-testid="close-register-btn"
              >
                Close Register
              </button>
            </>
          )}
        </div>
      </div>

      {/* Today's Transactions */}
      {status?.status === 'open' && status.transactions?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-800">Today's Cash Transactions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Description</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {status.transactions.map((tx, index) => (
                  <tr key={index} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {formatDate(tx.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tx.type === 'cash_in' ? 'bg-green-100 text-green-700' :
                        tx.type === 'cash_out' ? 'bg-red-100 text-red-700' :
                        'bg-slate-200 text-slate-700'
                      }`}>
                        {tx.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800">{tx.description}</td>
                    <td className={`px-4 py-3 text-sm font-medium text-right ${
                      tx.type === 'cash_in' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {tx.type === 'cash_in' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">Register History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-white">Date/Time</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-white">Opened By</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-white">Opening</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-white">Expected</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-white">Actual</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-white">Diff</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-white">Closed By</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-white">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    No history available
                  </td>
                </tr>
              ) : (
                history.map((record) => (
                  <tr key={record.id} className="hover:bg-slate-50">
                    <td className="px-3 py-3 text-xs">
                      <div className="text-slate-700">{new Date(record.opened_at || record.date).toLocaleDateString('en-IN')}</div>
                      <div className="text-slate-400">{new Date(record.opened_at || record.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">{record.opened_by || 'Owner'}</td>
                    <td className="px-3 py-3 text-xs text-right font-medium">{formatCurrency(record.opening_balance)}</td>
                    <td className="px-3 py-3 text-xs text-right">{record.closed_at ? formatCurrency(record.expected_cash) : '-'}</td>
                    <td className="px-3 py-3 text-xs text-right">{record.closed_at ? formatCurrency(record.physical_cash) : '-'}</td>
                    <td className={`px-3 py-3 text-xs text-right font-bold ${
                      (record.difference || 0) > 0 ? 'text-green-600' : (record.difference || 0) < 0 ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {record.closed_at ? (
                        <span>{(record.difference || 0) > 0 ? '+' : ''}{formatCurrency(record.difference)}</span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">
                      {record.closed_at ? (
                        <div>
                          <div>{record.closed_by || 'Owner'}</div>
                          <div className="text-slate-400">{new Date(record.closed_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      ) : (
                        <span className="text-amber-600">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {record.closed_at ? (
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => handleViewReport(record)}
                            className="px-2 py-1 bg-violet-100 text-violet-600 rounded text-xs hover:bg-violet-200"
                            data-testid={`view-report-${record.id}`}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleExportReportPDF(record)}
                            className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200"
                            data-testid={`export-report-${record.id}`}
                          >
                            PDF
                          </button>
                        </div>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Open</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Open Register Modal */}
      {showOpenModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold">Open Cash Register</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Opening Balance *</label>
                <input
                  type="number"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                  placeholder="Enter cash in drawer"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="opening-balance-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={openingNotes}
                  onChange={(e) => setOpeningNotes(e.target.value)}
                  rows="2"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="opening-notes-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowOpenModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleOpen}
                  className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                  data-testid="confirm-open-btn"
                >
                  Open Register
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Register Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold">Close Cash Register</h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="p-3 bg-violet-50 rounded-lg">
                <p className="text-sm text-violet-600">Expected Cash</p>
                <p className="text-2xl font-bold text-violet-700">{formatCurrency(status?.expected_cash)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Physical Cash Count *</label>
                <input
                  type="number"
                  value={physicalCash}
                  onChange={(e) => setPhysicalCash(e.target.value)}
                  placeholder="Enter actual cash in drawer"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="physical-cash-input"
                />
              </div>
              {physicalCash && (
                <div className={`p-3 rounded-lg ${
                  parseFloat(physicalCash) - (status?.expected_cash || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'
                }`}>
                  <p className="text-sm text-slate-600">Difference</p>
                  <p className={`text-xl font-bold ${
                    parseFloat(physicalCash) - (status?.expected_cash || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(parseFloat(physicalCash) - (status?.expected_cash || 0))}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Closing Notes</label>
                <textarea
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  rows="2"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="closing-notes-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCloseModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                  data-testid="confirm-close-btn"
                >
                  Close Register
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold">Add Cash Transaction</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Transaction Type</label>
                <select
                  value={transaction.type}
                  onChange={(e) => setTransaction({ ...transaction, type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="transaction-type-select"
                >
                  <option value="cash_in">Cash In</option>
                  <option value="cash_out">Cash Out</option>
                  <option value="petty_cash">Petty Cash</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                <input
                  type="number"
                  value={transaction.amount}
                  onChange={(e) => setTransaction({ ...transaction, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="transaction-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={transaction.description}
                  onChange={(e) => setTransaction({ ...transaction, description: e.target.value })}
                  placeholder="e.g., Change for customer"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="transaction-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={transaction.reference}
                  onChange={(e) => setTransaction({ ...transaction, reference: e.target.value })}
                  placeholder="Optional"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="transaction-reference-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransaction}
                  className="flex-1 bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700"
                  data-testid="confirm-transaction-btn"
                >
                  Add Transaction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Previous Register Modal */}
      {showClosePreviousModal && unclosedRegister && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-4 border-b border-slate-200 bg-amber-50">
              <h3 className="text-lg font-semibold text-amber-800">Close Previous Register</h3>
              <p className="text-sm text-amber-600 mt-1">
                Date: {new Date(unclosedRegister.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="p-4 space-y-4">
              {/* Cash Calculation Summary */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Opening Balance:</span>
                  <span className="font-medium">{formatCurrency(previousRegisterSummary?.opening_balance || unclosedRegister.opening_balance)}</span>
                </div>
                {previousRegisterSummary?.sales && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>+ Cash Sales:</span>
                      <span>{formatCurrency(previousRegisterSummary.sales?.by_mode?.cash || 0)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>+ Cash In (Manual):</span>
                      <span>{formatCurrency(previousRegisterSummary.transactions?.cash_in || 0)}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>- Cash Expenses:</span>
                      <span>{formatCurrency(previousRegisterSummary.expenses?.by_mode?.cash || 0)}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>- Cash Out (Manual):</span>
                      <span>{formatCurrency(previousRegisterSummary.transactions?.cash_out || 0)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between pt-2 border-t border-slate-200 font-bold text-violet-700">
                  <span>Expected Cash:</span>
                  <span>{formatCurrency(previousRegisterSummary?.expected_cash || unclosedRegister.expected_cash || unclosedRegister.opening_balance)}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Actual Cash Count *</label>
                <input
                  type="number"
                  value={previousPhysicalCash}
                  onChange={(e) => setPreviousPhysicalCash(e.target.value)}
                  placeholder="Enter the cash that was in drawer"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="previous-physical-cash-input"
                />
              </div>
              
              {previousPhysicalCash && (
                <div className={`p-3 rounded-lg ${
                  parseFloat(previousPhysicalCash) - (previousRegisterSummary?.expected_cash || unclosedRegister.expected_cash || unclosedRegister.opening_balance) >= 0 
                    ? 'bg-green-50' 
                    : 'bg-red-50'
                }`}>
                  <p className="text-sm text-slate-600">Difference</p>
                  <p className={`text-xl font-bold ${
                    parseFloat(previousPhysicalCash) - (previousRegisterSummary?.expected_cash || unclosedRegister.expected_cash || unclosedRegister.opening_balance) >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {formatCurrency(parseFloat(previousPhysicalCash) - (previousRegisterSummary?.expected_cash || unclosedRegister.expected_cash || unclosedRegister.opening_balance))}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Closing Notes</label>
                <textarea
                  value={previousClosingNotes}
                  onChange={(e) => setPreviousClosingNotes(e.target.value)}
                  rows="2"
                  placeholder="Any notes about the variance or issues"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="previous-closing-notes-input"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowClosePreviousModal(false);
                    setPreviousPhysicalCash('');
                    setPreviousClosingNotes('');
                    setPreviousRegisterSummary(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClosePreviousRegister}
                  className="flex-1 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700"
                  data-testid="confirm-close-previous-btn"
                >
                  Close Previous Register
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Close Report Modal */}
      {showCloseReportModal && selectedCloseReport && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 bg-slate-800 text-white sticky top-0 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Cash Register Close Report</h3>
                <p className="text-sm text-slate-300">
                  {new Date(selectedCloseReport.opened_at || selectedCloseReport.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <button
                onClick={() => handleExportReportPDF(selectedCloseReport)}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
              >
                Export PDF
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Register Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Opened By</p>
                  <p className="font-semibold">{selectedCloseReport.opened_by || 'Owner'}</p>
                  <p className="text-xs text-slate-400">{new Date(selectedCloseReport.opened_at).toLocaleString('en-IN')}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Closed By</p>
                  <p className="font-semibold">{selectedCloseReport.closed_by || 'Owner'}</p>
                  <p className="text-xs text-slate-400">{new Date(selectedCloseReport.closed_at).toLocaleString('en-IN')}</p>
                </div>
              </div>

              {/* Summary from closing_summary */}
              {selectedCloseReport.closing_summary && (
                <>
                  {/* Sales Summary */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <h4 className="font-semibold text-green-800 mb-2">Sales Summary ({selectedCloseReport.closing_summary.sales?.count || 0} bills)</h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div className="flex justify-between"><span>Cash Sales:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.sales?.by_mode?.cash)}</span></div>
                      <div className="flex justify-between"><span>UPI Sales:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.sales?.by_mode?.upi)}</span></div>
                      <div className="flex justify-between"><span>Card Sales:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.sales?.by_mode?.card)}</span></div>
                      <div className="flex justify-between"><span>Credit Sales:</span><span className="font-medium text-orange-600">{formatCurrency(selectedCloseReport.closing_summary.sales?.by_mode?.credit)}</span></div>
                    </div>
                    <div className="flex justify-between pt-2 mt-2 border-t border-green-200 font-bold text-green-800">
                      <span>Total Sales:</span>
                      <span>{formatCurrency(selectedCloseReport.closing_summary.sales?.total)}</span>
                    </div>
                  </div>

                  {/* Expenses Summary */}
                  <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                    <h4 className="font-semibold text-red-800 mb-2">Expenses Summary ({selectedCloseReport.closing_summary.expenses?.count || 0} entries)</h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div className="flex justify-between"><span>Cash Expenses:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.expenses?.by_mode?.cash)}</span></div>
                      <div className="flex justify-between"><span>Bank Expenses:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.expenses?.by_mode?.bank)}</span></div>
                      <div className="flex justify-between"><span>UPI Expenses:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.expenses?.by_mode?.upi)}</span></div>
                    </div>
                    <div className="flex justify-between pt-2 mt-2 border-t border-red-200 font-bold text-red-800">
                      <span>Total Expenses:</span>
                      <span>{formatCurrency(selectedCloseReport.closing_summary.expenses?.total)}</span>
                    </div>
                  </div>

                  {/* Supplier Payments */}
                  {selectedCloseReport.closing_summary.supplier_payments?.total > 0 && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h4 className="font-semibold text-slate-800 mb-2">Supplier Payments</h4>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <div className="flex justify-between"><span>Cash:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.supplier_payments?.by_mode?.cash)}</span></div>
                        <div className="flex justify-between"><span>Bank:</span><span className="font-medium">{formatCurrency(selectedCloseReport.closing_summary.supplier_payments?.by_mode?.bank)}</span></div>
                      </div>
                      <div className="flex justify-between pt-2 mt-2 border-t border-slate-300 font-bold text-slate-800">
                        <span>Total Supplier Payments:</span>
                        <span>{formatCurrency(selectedCloseReport.closing_summary.supplier_payments?.total)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Closing Balance */}
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                <h4 className="font-semibold text-violet-800 mb-3">Closing Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Opening Balance:</span><span className="font-medium">{formatCurrency(selectedCloseReport.opening_balance)}</span></div>
                  <div className="flex justify-between"><span>Expected Cash:</span><span className="font-medium">{formatCurrency(selectedCloseReport.expected_cash)}</span></div>
                  <div className="flex justify-between"><span>Actual Cash (Physical):</span><span className="font-medium">{formatCurrency(selectedCloseReport.physical_cash)}</span></div>
                  <div className={`flex justify-between pt-2 mt-2 border-t border-violet-300 font-bold text-lg ${
                    (selectedCloseReport.difference || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <span>Difference:</span>
                    <span>
                      {(selectedCloseReport.difference || 0) >= 0 ? '+' : ''}{formatCurrency(selectedCloseReport.difference)}
                      <span className="text-sm font-normal ml-2">
                        {(selectedCloseReport.difference || 0) > 0 ? '(Excess)' : (selectedCloseReport.difference || 0) < 0 ? '(Short)' : '(Exact)'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {selectedCloseReport.closing_notes && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-600 font-medium">Notes</p>
                  <p className="text-sm text-amber-800">{selectedCloseReport.closing_notes}</p>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => { setShowCloseReportModal(false); setSelectedCloseReport(null); }}
                  className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRegister;
